<script setup lang="ts">
import { AArrowUp, ArrowLeftRight, Bean, Bolt, BookOpenText, ChevronDown, Dices, Eraser, EyeOff, FlaskConical, ImageIcon, ImageUpscale, Infinity, Info, InfoIcon, Layers, Lightbulb, LoaderCircle, MessageSquareMore, MessageSquareText, Palette, Pickaxe, PlaneTakeoff, Plus, Proportions, Settings2, Shirt, Signature, Smile, Sparkles, Ticket, Turtle, WandSparkles, X } from 'lucide-vue-next'
import { computed, defineAsyncComponent, nextTick, ref, shallowRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { addYears } from 'date-fns'
import { useConfirm } from 'primevue/useconfirm'
import ConfirmPopup from 'primevue/confirmpopup'
import PromptInput from '@/components/generator/common/PromptInput.vue'
import ModifierSlot from '@/components/generator/generator/modifiers/ModifierSlot.vue'
import Tools from '@/utils/tools'
import { useDynamicModalStore } from '@/stores/dynamicModalStore'
import { DynamicModalType, ENotificationType, SubscriptionType } from '@/types'
import { useGeneratorStore } from '@/stores/GeneratorStore'
import { CGeneratorCreativity, CGeneratorFormat, CGeneratorResolution } from '@/constants'
import { useResizeListener } from '@/composables/useResizeListener'
import { useAuthStore } from '@/stores/authStore'
import { MaxFailedTaskError } from '@/errors/FapFapError'
import { useNotificationStore } from '@/stores/notificationStore'
import GeneratorTemplate from '@/components/generator/GeneratorTemplate.vue'
import GeneratorPanel from '@/components/generator/common/GeneratorPanel.vue'
import { useGraphicStyles } from '@/composables/useGraphicStyles'
import EnergyIcon from '@/icons/EnergyIcon.vue'

const confirm = useConfirm()
const { t } = useI18n()
const GeneratorGallery = defineAsyncComponent(() => import('@/components/generator/generator/GeneratorGallery.vue'))
const MultiChoice = defineAsyncComponent(() => import('@/components/generator/common/GeneratorMultipleChoice.vue'))
const GeneratorSwitch = defineAsyncComponent(() => import('@/components/generator/common/GeneratorSwitch.vue'))
const GeneratorSeed = defineAsyncComponent(() => import('@/components/generator/common/GeneratorSeed.vue'))
const GeneratorPresets = defineAsyncComponent(() => import('@/components/generator/generator/GeneratorPresets.vue'))
const ProductTour = defineAsyncComponent(() => import('@/components/generator/generator/ProductTour.vue'))

const router = useRouter()
const genStore = useGeneratorStore()
const authStore = useAuthStore()
const { computedScreenWidth } = useResizeListener()
const {
	generatorPrompt: data,
	selectedStyle,
	disableCreate,
	running,
	textLimitPos,
	textLimitNeg,
	settingCost,
	cost,
	nextReset,
	watermarkCost,
	maxTask,
	newModifiers,
	newModifiersCount,
} = storeToRefs(genStore)
const { user, isPremium, hasDailyTickets } = storeToRefs(authStore)

const timer = Tools.createTimer(0, 1010)

const blink = shallowRef<boolean>(false)
const showPreset = shallowRef<boolean>(false)
const gen_gallery = ref()
const isMobile = computed(() => computedScreenWidth.value < 1024)
const generatedPrompt = ref<string>('')
const aiPromptGenerating = shallowRef<boolean>(false)
const waitAIPrompt = shallowRef<boolean>(false)
const showAIAssistant = shallowRef<boolean>(false)
const lockAIAssistant = shallowRef<boolean>(false)
const genToLaunch = shallowRef<number>(1)
const largerFont = shallowRef<boolean>(Tools.getCookie('larger-font') === 'true')
const discordLink = ref<string>(import.meta.env.VITE_DISCORD_INVITE_LINK ?? '')

function updateGenToLaunch(value: number) {
	if (user.value.plan >= 1 && !data.value.seedLock) {
		genToLaunch.value = value
	}
}

watch(() => data.value.seedLock, (seedLock: boolean | undefined) => {
	if (seedLock) {
		genToLaunch.value = 1
	}
})

const promptValue = computed({
	get: () => {
		if (aiPromptGenerating.value) {
			return data.value.prompt
		}
		if (waitAIPrompt.value) {
			return generatedPrompt.value
		}
		return data.value.prompt
	},
	set: (val: string) => {
		data.value.prompt = val
	},
})

const placeholderPos = computed(() => {
	return '1girl, beach, arms up, standing'
})

const enoughTickets = computed(() => {
	return (user.value.generation_token >= cost.value || (user.value.daily_token >= cost.value && isPremium.value))
		|| !useAuthStore().isAuth()
})

async function generate() {
	if (!selectedStyle.value) {
		useDynamicModalStore().showModal(DynamicModalType.NoStyleSelected, {
			props: {
				type: 'generate',
			},
		})
		return
	}
	if (!authStore.isAuth()) {
		router.push('/account/signup?rel=create')
		return
	}
	for (let i = 0; i < genToLaunch.value; i++) {
		try {
			if (!enoughTickets.value) {
				return router.push({
					name: 'Ticket',
				})
			}
			if (waitAIPrompt.value) {
				useNotificationStore().push(ENotificationType.AppError, {
					key: `notification.ai_autocompletion_block_gen`,
					delay: 4000,
				})
				return
			}
			await genStore.generate()
			await nextTick()
		}
		catch (err) {
			if (err instanceof MaxFailedTaskError) {
				timer.remaining.value = err.data.remaining_time

				timer.start()
			}
		}
	}
}

function selectModifiers() {
	if (!selectedStyle.value) {
		useDynamicModalStore().showModal(DynamicModalType.NoStyleSelected, {
			props: {
				type: 'modifier',
			},
		})
		return
	}
	useDynamicModalStore().showModal(DynamicModalType.GeneratorModifiers, {
		props: {
			mode: 'generator',
		},
	})
}

function playBlink(): void {
	blink.value = true

	setTimeout(() => {
		blink.value = false
	}, 1000)
}

function clearPrompts() {
	data.value.prompt = ''
	data.value.neg_prompt = ''
}

watch(() => largerFont.value, () => {
	Tools.setCookie('larger-font', largerFont.value.toString(), addYears(new Date(), 1), `.${import.meta.env.VITE_DOMAIN}`, '/')
})

function toggleInfoReset(event: any) {
	confirm.require({
		target: event.currentTarget,
		group: 'info-reset',
	})
}

async function completeWithAIAssistant(type: string): Promise<void> {
	lockAIAssistant.value = true
	setTimeout(() => {
		lockAIAssistant.value = false
	}, 5200)
	if (!authStore.isAuth()) {
		router.push('/account/signin')
		return
	}
	try {
		aiPromptGenerating.value = true
		waitAIPrompt.value = true
		if (type === 'auto-complete') {
			generatedPrompt.value = await genStore.completeTagsAI()
		}
		if (type === 'convert-nl') {
			generatedPrompt.value = await genStore.convertNaturalLanguage()
		}
	}
	catch {
		waitAIPrompt.value = false
	}
	finally {
		aiPromptGenerating.value = false
	}
}

function replacePrompt(): void {
	data.value.prompt = generatedPrompt.value
	waitAIPrompt.value = false
}

const hasNewStyles = computed(() => {
	return useGraphicStyles().getStyleGenerator().some(({ is_new }) => is_new)
})

watch(() => data.value.prompt, () => {
	if (!data.value.prompt.length) {
		showAIAssistant.value = false
	}
})
</script>

<template>
	<ProductTour v-if="authStore.isAuth()" />
	<GeneratorTemplate
		:show-left-container="showPreset && !isMobile"
	>
		<template #leftContainer>
			<GeneratorPresets @inject="playBlink" />
		</template>
		<template #rightContainer>
			<div class="flex flex-col gap-6 w-full">
				<div class="flex gap-2 -mb-4">
					<router-link
						to="/guide"
						class="button-light-purple no-underline w-fit"
					>
						{{ t('common.guide') }}
						<BookOpenText class="lucide-button-icon" />
					</router-link>
					<a
						target="_blank"
						:href="discordLink"
						class="button-light-blue no-underline w-fit"
					>
						Discord
						<i class="pi pi-discord" />
					</a>
				</div>
				<div class="setting-container">
					<div
						class="bg-slate-800/70 border-[1px] h-full w-6/12 border-solid border-slate-700/60 rounded-xl p-2 md:p-4 flex items-center justify-between cursor-pointer hover:bg-slate-700/70 transition-colors duration-200"
						@click="showPreset = !showPreset"
					>
						<div class="flex-center gap-3">
							<div class="w-[40px] aspect-[512/704] rounded-lg flex-center bg-slate-950/30">
								<Bolt class="lucide-button-icon" />
							</div>
							<div class="flex flex-col items-start">
								<span class="text-slate-200 font-medium text-sm">{{ t('generator.presets.label') }}</span>
								<span class="text-violet-300 text-xsp">{{ t(`common.${showPreset ? 'close' : 'open'}`) }}</span>
							</div>
						</div>
						<div class="flex-center">
							<ChevronDown class="lucide-button-icon-xs text-slate-400" />
						</div>
					</div>
					<div
						id="generator-style"
						class="bg-slate-800/70 border-[2px] h-full w-6/12 border-dashed border-purple-600 rounded-xl p-2 md:p-4 flex items-center justify-between cursor-pointer hover:bg-slate-700/70 transition-colors duration-200 relative"
						:class="{
							'!border-slate-700/60 !border-solid': selectedStyle,
						}"
						@click="useDynamicModalStore().showModal(DynamicModalType.SelectGenStyle, {
							props: {
								mode: 'generator',
							},
						})"
					>
						<div
							v-if="hasNewStyles && selectedStyle"
							v-tooltip.right="{ value: t('common.new_style'), hideDelay: 500 }"
							class="absolute top-3 right-3 w-3 h-3 bg-red-500 rounded-full animate-pulse"
						/>

						<div
							class="flex-center gap-3"
						>
							<div class="relative flex-center">
								<img
									v-if="selectedStyle"
									width="40"
									class="rounded-lg object-cover border-[1px] border-solid border-slate-600/50"
									:src="genStore.getStylePreview(selectedStyle).webp"
								>
								<div
									v-else
									width="40"
									class="rounded-lg aspect-[512/704] flex-center w-[40px] border-[1px] border-solid border-slate-600/50"
								>
									<ImageIcon class="text-slate-400" />
								</div>
							</div>
							<div class="flex flex-col items-start">
								<span
									v-if="selectedStyle"
									class="text-slate-200 font-medium text-sm"
								>{{ t('common.style') }}</span>
								<span
									v-else
									class="text-slate-200 font-black text-base"
								>{{ t('generator.choose_style') }}</span>
								<span
									v-if="selectedStyle"
									class="text-violet-300 text-xsp"
								>{{ selectedStyle.lib }}</span>
							</div>
						</div>
						<div
							v-if="selectedStyle"
							class="flex-center"
						>
							<ChevronDown class="lucide-button-icon-xs text-slate-400" />
						</div>
					</div>
				</div>
				<div v-if="showPreset && isMobile">
					<GeneratorPresets @inject="playBlink" />
				</div>

				<div class="prompt-container-compact">
					<PromptInput
						id="positive-prompt"
						v-model="promptValue"
						:title="t('generator.prompt')"
						:icon="MessageSquareText"
						:placeholder="placeholderPos"
						:text-limit="textLimitPos"
						:limit-message="t('generator.lengthy_prompt')"
						:disabled="waitAIPrompt"
						:use-larger-font="largerFont"
						:is-blinking="blink"
						type="positive"
					>
						<!-- Header actions slot - version compacte -->
						<template #header-actions>
							<div class="prompt-actions-header">
								<div class="action-group">
									<Button
										v-if="!aiPromptGenerating"
										v-tooltip.bottom="{ value: t('tooltips.larger_font'), showDelay: 500 }"
										class="action-btn-mini"
										:class="{ active: largerFont }"
										@click="largerFont = !largerFont"
									>
										<AArrowUp class="lucide-button-icon-xss" />
									</Button>
									<Button
										v-if="!aiPromptGenerating"
										v-tooltip.bottom="{ value: t('tooltips.random_prompts'), showDelay: 500 }"
										class="action-btn-mini"
										@click="genStore.getRandomPreset"
									>
										<Dices class="lucide-button-icon-xss" />
									</Button>
									<Button
										v-if="!aiPromptGenerating"
										v-tooltip.bottom="{ value: t('tooltips.clear_prompts'), showDelay: 500 }"
										class="action-btn-mini"
										@click="clearPrompts"
									>
										<Eraser class="lucide-button-icon-xss" />
									</Button>
								</div>

								<LoaderCircle
									v-if="aiPromptGenerating"
									class="lucide-button-icon-xs animate-spin text-purple-400"
								/>
							</div>
						</template>

						<!-- Footer actions slot - version compacte -->
						<template #footer-actions>
							<div class="prompt-actions-footer">
								<Button
									v-if="!waitAIPrompt && data.prompt.length"
									id="switch-prompt-natural"
									class="button-light-purple"
									@click="showAIAssistant = !showAIAssistant"
								>
									<component
										:is="showAIAssistant ? X : Sparkles"
										class="lucide-button-icon-xs"
									/>
									<span class="whitespace-nowrap">
										{{ t('generator.ai_assistant.title') }}
									</span>
								</Button>

								<template v-else-if="waitAIPrompt">
									<div class="ai-actions">
										<Button
											class="ai-action-btn revert"
											:disabled="aiPromptGenerating"
											@click="waitAIPrompt = false"
										>
											{{ t('common.revert') }}
										</Button>
										<Button
											class="ai-action-btn accept"
											:disabled="aiPromptGenerating"
											@click="replacePrompt"
										>
											{{ t('common.accept') }}
										</Button>
									</div>
								</template>
								<Transition name="list">
									<div
										v-if="!waitAIPrompt && showAIAssistant"
										class="flex flex-col gap-2 mx-auto mt-4"
									>
										<Button
											v-if="!aiPromptGenerating"
											id="ai-prompt-generator"
											v-tooltip.bottom="{ value: t('tooltips.ac_prompt'), showDelay: 500 }"
											class="button-purple"
											:disabled="lockAIAssistant"
											@click="completeWithAIAssistant('auto-complete')"
										>
											<Sparkles
												v-if="!lockAIAssistant"
												class="lucide-button-icon-xs"
											/>
											<LoaderCircle
												v-else
												class="lucide-button-icon-xs animate-spin"
											/>
											{{ t('generator.ai_assistant.ac_tags') }}
										</Button>
										<Button
											v-if="!aiPromptGenerating"
											id="ai-prompt-generator"
											v-tooltip.bottom="{ value: t('tooltips.nl_prompt'), showDelay: 500 }"
											class="button-purple"
											:disabled="lockAIAssistant"
											@click="completeWithAIAssistant('convert-nl')"
										>
											<ArrowLeftRight
												v-if="!lockAIAssistant"
												class="lucide-button-icon-xs"
											/>
											<LoaderCircle
												v-else
												class="lucide-button-icon-xs animate-spin"
											/>
											{{ t('generator.ai_assistant.nl_tags') }}
										</Button>
									</div>
								</Transition>
							</div>
						</template>
					</PromptInput>

					<PromptInput
						id="negative-prompt"
						v-model="data.neg_prompt"
						:title="t('generator.neg_prompt')"
						:icon="MessageSquareMore"
						placeholder="1boy, sitting, indoors"
						:text-limit="textLimitNeg"
						:limit-message="t('generator.lengthy_prompt')"
						:use-larger-font="largerFont"
						:is-blinking="blink"
						:info="t('generator.info_neg_prompt')"
						type="negative"
					/>
				</div>

				<GeneratorPanel
					id="generator-modifiers"
					:title="t('boosters.title', { count: data.modifiers.length })"
					:icon="FlaskConical"
				>
					<template #headerActions>
						<div
							v-if="newModifiers"
							class="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-300 rounded-lg shadow-lg"
						>
							<div class="w-2 h-2 bg-purple-400 rounded-full" />
							<span class="text-xs font-medium">{{ newModifiersCount }} {{ t(`common.new${newModifiersCount > 1 ? 's' : ''}`) }}</span>
						</div>

						<Button
							class="button-purple !p-1 relative"
							@click="selectModifiers"
						>
							<Plus class="lucide-button-icon" />
						</Button>
					</template>
					<template #content>
						<div class="flex flex-col gap-1 justify-between w-11/12">
							<span
								v-if="!data.modifiers.length"
								class="text-slate-300 italic text-xsp pl-1"
							>
								{{ t('boosters.boosters_info') }}
							</span>
							<div
								v-else
								class="mt-auto flex flex-col flex-nowrap gap-2"
							>
								<TransitionGroup
									name="list"
									:css="true"
								>
									<div
										v-for="(modifier, index) in data.modifiers"
										:key="index"
									>
										<ModifierSlot
											:modifier="modifier"
											@update:weight="genStore.updateModifierWeight(index, $event)"
											@remove="genStore.removeModifier(modifier.id)"
										/>
									</div>
								</TransitionGroup>
							</div>

							<router-link
								v-tooltip="t('common.guide')"
								to="/guide#modifiers"
								class="hyperlink-discret ml-auto mt-2"
							>
								<BookOpenText class="lucide-button-icon" />
							</router-link>
						</div>
					</template>
				</GeneratorPanel>

				<GeneratorPanel
					:title="t('common.settings')"
					:icon="Settings2"
				>
					<template #headerActions>
						<span
							class="flex-center gap-1 text-sm font-medium"
							:class="settingCost ? '' : 'text-slate-300'"
						>
							<span>
								{{ settingCost ? '+' : '' }}{{ settingCost }}
							</span>

							<component
								:is="hasDailyTickets ? EnergyIcon : Ticket"
								class="lucide-button-icon-xs"
								v-bind="{ variant: 'normal' }"
							/>

						</span>
					</template>
					<template #content>
						<MultiChoice
							id="generator-resolution"
							v-model="data.quality"
							:title="t('generator.image_resolution.label')"
							:icon="Layers"
							:choices="CGeneratorResolution"
							:info="t('generator.info_resolution')"
						/>
						<MultiChoice
							id="generator-format"
							v-model="data.format"
							:title="t('generator.image_format.label')"
							:icon="Proportions"
							:choices="CGeneratorFormat"
							:info="t('generator.info_format')"
						/>

						<MultiChoice
							id="generator-creativity"
							v-model="data.creativity"
							:title="t('generator.image_creativity.label')"
							:icon="Lightbulb"
							:choices="CGeneratorCreativity"
							:info="t('generator.info_creativity')"
						/>
						<GeneratorSwitch
							id="generator-face-restore"
							v-model="data.automatic_face_restore"
							:icon="Smile"
							:title="t('generator.auto_face_restore')"
							:info="t('generator.info_auto_face_restore')"
							:cost="0"
						/>
						<GeneratorSwitch
							id="generator-less-nsfw"
							v-model="data.less_nsfw"
							:icon="Shirt"
							:title="t('generator.less_nsfw')"
							:info="t('generator.info_less_nsfw')"
							:cost="0"
						/>
						<GeneratorSwitch
							id="generator-upscale"
							v-model="data.upscale"
							:icon="ImageUpscale"
							:title="t('common.upscale')"
							:cost="0"
							:disabled="data.slow_mode"
							:info="t('generator.info_upscale')"
							guide="#upscale"
						/>
						<GeneratorSwitch
							id="generator-fastlane"
							v-model="data.fastLane"
							:icon="PlaneTakeoff"
							:title="t('generator.fast_lane')"
							:cost="4"
							:disabled="data.slow_mode"
							:info="t('generator.info_fast_lane')"
							guide="#fastlane"
						/>
						<GeneratorSwitch
							v-if="!isPremium"
							v-model="data.watermark"
							:icon="Signature"
							:title="t('generator.remove_watermark')"
							:cost="watermarkCost"
							:pro="SubscriptionType.Basic"
							:pro-optional="true"
							:info="t('generator.info_remove_watermark')"
							guide="#watermark"
						/>
						<GeneratorSwitch
							id="generator-slowmode"
							v-model="data.slow_mode"
							:icon="Turtle"
							:title="t('generator.eco_mode')"
							:cost="0"
							:pro="SubscriptionType.Pro"
							:disabled="data.fastLane"
							:info="t('generator.info_eco_mode')"
							guide="#slowmode"
						/>
						<GeneratorSwitch
							id="generator-private"
							v-model="data.private"
							:icon="EyeOff"
							:title="Tools.capitalizeFirstLetter(t('common.private'))"
							:cost="0"
							:pro="SubscriptionType.Basic"
							:info="t('generator.info_private')"
						/>
						<GeneratorSeed
							id="generator-seed"
							v-model="data"
							:icon="Bean"
							:title="t('generator.seed')"
						/>
					</template>
				</GeneratorPanel>
			</div>

			<div class="z-[1] flex-center flex-col gap-2 sticky py-6 gen-lg:mt-auto w-full bottom-0 bg-slate-900 border-0 border-t-2 border-solid border-slate-800">
				<div
					v-if="isPremium"
					class="w-full h-[24px] relative"
				>
					<div class="bg-blue-900 w-full h-full rounded absolute" />
					<div
						class="h-full absolute bg-blue-600 rounded"
						:style="{ width: `${((data.slow_mode ? 1200 : user.daily_token) / (user.plan === SubscriptionType.Basic ? 350 : 1200)) * 100}%` }"
					/>
					<div class="absolute px-2 font-semibold text-xsp flex items-center justify-between w-full top-1/2 -translate-y-1/2">
						<span class="flex-center gap-1">
							<Button
								v-if="hasDailyTickets"
								class="reset-btn"
								@click="toggleInfoReset"
							>
								<Info class="lucide-button-icon" />
							</Button>

							{{ t('common.energy') }}
						</span>

						<span
							v-if="hasDailyTickets && !data.slow_mode"
							class="flex-center gap-1"
						>
							{{ user.daily_token }} / {{ user.plan === SubscriptionType.Basic ? 350 : 1200 }}
							<component
								:is="hasDailyTickets ? EnergyIcon : Ticket"
								v-bind="{ variant: 'normal' }"
								class="lucide-button-icon-xs"
							/>
						</span>
						<span
							v-else-if="data.slow_mode"
							class="flex-center gap-1"
						>
							{{ t('generator.eco_mode') }} <Infinity class="lucide-button-icon" />
						</span>
						<span
							v-else
							class="flex-center gap-1"
						>
							{{ t('reset_timer', { timer: nextReset }) }}
						</span>
					</div>
				</div>

				<router-link
					v-if="!isPremium"
					to="/tickets"
					class="no-underline select-none duration-300 hover:bg-slate-700 cursor-pointer hover:text-white text-xsp ml-auto text-slate-300 flex-center gap-1 border-[1px] border-slate-600 border-solid py-1 px-2 rounded-full"
				>
					<InfoIcon class="lucide-button-icon-xs text-slate-300" />
					{{ t('common.unlock_unlimited') }}
				</router-link>

				<ConfirmPopup
					group="info-reset"
				>
					<template #container>
						<div class="flex-center gap-1 text-sm p-2">
							{{ t('reset_timer', { timer: nextReset }) }}
						</div>
					</template>
				</ConfirmPopup>
				<div class="w-full flex-center gap-1 h-[56px]">
					<div class="text-xs flex-center flex-col w-fit gap-1">
						<span
							v-tooltip.right="{ value: t('tooltips.generation_progress'), showDelay: 500 }"
							class="bg-slate-800 flex items-center px-1 gap-1 rounded py-1 w-[50px]"
							:class="{ 'animate-skeleton !bg-purple-600': running.generation }"
						>
							<Pickaxe class="lucide-button-icon" />

							{{ running.generation }}/{{ maxTask }}
						</span>
						<span
							v-tooltip.right="{ value: t('tooltips.upscale_progress'), showDelay: 500 }"
							class="bg-slate-800 flex items-center px-1 gap-1 rounded py-1 w-[50px]"
							:class="{ 'animate-skeleton !bg-purple-600': running.upscale }"
						>
							<ImageUpscale class="lucide-button-icon" />
							{{ running.upscale }}
						</span>
					</div>
					<div
						v-tooltip.right="data.seedLock ? { value: t('tooltips.unlock_seed_multigen'), showDelay: 300 } : null"
						class="grid grid-cols-2 overflow-hidden w-[60px] rounded bg-slate-700 h-full cursor-pointer text-sm font-medium"
					>
						<span
							class="w-full flex-center rounded-br hover:bg-slate-800 duration-300"
							:class="{ 'bg-slate-500': genToLaunch === 1 }"
							@click="updateGenToLaunch(1)"
						>1</span>
						<span
							class="w-full flex-center rounded-bl hover:bg-slate-800 duration-300"
							:class="{ 'bg-slate-500': genToLaunch === 2, 'opacity-50': user.plan < SubscriptionType.Basic || data.seedLock }"
							@click="updateGenToLaunch(2)"
						>2</span>
						<span
							class="w-full flex-center rounded-tr hover:bg-slate-800 duration-300"
							:class="{ 'bg-slate-500': genToLaunch === 3, 'opacity-50': user.plan < SubscriptionType.Pro || data.seedLock }"
							@click="updateGenToLaunch(3)"
						>3</span>
						<span
							class="w-full flex-center rounded-tl hover:bg-slate-800 duration-300"
							:class="{ 'bg-slate-500': genToLaunch === 4, 'opacity-50': user.plan < SubscriptionType.Pro || data.seedLock }"
							@click="updateGenToLaunch(4)"
						>4</span>
					</div>
					<Button
						class="button-shadow w-full h-full !py-2 transform active:scale-x-90 transition-transform"
						:class="{
							'button-rainbow-shade': (user.daily_token < cost || !isPremium),
							'button-blue-shade': isPremium && user.daily_token >= cost,
						}"
						:disabled="(disableCreate || timer.remaining.value > 0) && enoughTickets"
						@click="generate"
					>
						<template #default>
							<span
								v-if="timer.remaining.value <= 0"
								class="absolute top-1/2 -translate-y-1/2 right-2 flex-center gap-0 text-sm font-semibold"
							>
								{{ cost * genToLaunch }}
								<component
									:is="hasDailyTickets ? EnergyIcon : Ticket"
									class="lucide-button-icon"
									v-bind="{ variant: 'normal' }"
								/>
							</span>
							<span
								class="flex-center gap-1 w-full font-extrabold text-lg"
								:class="{ '!text-sm': timer.remaining.value > 0 }"
							>
								<template v-if="!enoughTickets">
									{{ t('common.buy_tickets') }}
								</template>
								<template v-else-if="timer.remaining.value <= 0">
									<LoaderCircle
										v-if="disableCreate"
										class="lucide-button-icon-sm animate-spin"
									/>
									<WandSparkles
										v-else
										class="lucide-button-icon"
									/>
									<span>{{ t('common.create') }}</span>
								</template>
								<div
									v-else
									class="flex items-center text-start flex-nowrap w-full justify-between"
								>
									<span>{{ t('generator.many_failures') }}</span>
									<span>
										{{ timer.remaining.value.toFixed(2) }}s
									</span>
								</div>

							</span>
						</template>
					</Button>
				</div>
			</div>
		</template>
		<template #content>
			<GeneratorGallery ref="gen_gallery" />
		</template>
	</GeneratorTemplate>
</template>

<style lang="scss">

</style>

<style scoped lang="scss">
.setting-container {
    @apply flex w-[100%] gap-4 h-[90px];
}

.small-info {
    @apply text-xsp text-slate-300;
}

.orange-slider :deep(.p-slider-range) {
    @apply bg-orange-500
}

.orange-slider :deep(.p-slider-handle) {
	@apply bg-orange-500;
    &:focus-within::before {
        @apply bg-orange-700;
    }
}

@keyframes blink {
  0%, 100% { @apply border-slate-500 }
  50% { @apply border-yellow-500 } /* Change la couleur selon ton besoin */
}

.animate-blink {
  animation: blink 0.2s linear 3;
}
</style>
